import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import CustomTextInput from '../../components/formComponents/CustomTextInput';
import CustomPasswordInput from '../../components/formComponents/CustomPassword';
import CustomCheckbox from '../../components/formComponents/CustomCheckbox';
import CustomButton from '../../components/common/CustomButton';
import { Link, useNavigate } from "react-router-dom";
import { merchantAuthController } from "../../controllers/authController";
import { toast } from "react-toastify";
import { Turnstile, Honeypot, turnstileConfigured } from "../../components/Turnstile";


interface FormData {
	name: string;
	email: string;
	password: string;
	termsConditons: boolean;
}

const RegisterForm = () => {
	const navigate = useNavigate();
	const [state, setState] = useState({
		loading: false,
	});
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const [company, setCompany] = useState(""); // honeypot
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FormData>();

	const onSubmit = async (data: FormData) => {
		try {
			setState((prev) => ({ ...prev, loading: true }));

			if (!data.termsConditons) {
				toast.error("We need you to accept our terms and conditions!");
				return;
			}

			if (!data.name || !data.email || !data.password) {
				toast.error("Please fill all necessary fields!");
				return;
			}

			// Honeypot: a real user never fills the hidden "company" field.
			if (company.trim() !== "") {
				return;
			}

			if (turnstileConfigured && !turnstileToken) {
				toast.error("Please complete the verification challenge.");
				return;
			}

			// Split company name into first and last name for API compatibility
			const nameParts = data.name.split(" ");
			const firstName = nameParts[0];
			const lastName = nameParts.slice(1).join(" ") || firstName;

			const response = await merchantAuthController.registerMerchant({
				firstName,
				lastName,
				email: data.email,
				password: data.password
			}, turnstileToken);

			// Check if we have a successful response with data
			if (response?.data?.data?.requestId) {
				// TEMPORARY: merchant email-OTP is bypassed (backend auto-verifies on
				// register). Skip the /verifyEmail step and send them straight to login.
				// To re-enable OTP, restore the navigate("/verifyEmail", ...) below.
				toast.success("Registration successful! Please log in.");
				navigate("/login");
			} else {
				throw new Error("Invalid response format");
			}
		} catch (error: any) {
			console.error('Registration Error:', error.response?.data);
			// Extract error message from the API response
			const errorResponse = error.response?.data;
			let errorMessage = "Registration failed. Please try again.";

			if (errorResponse?.errors && errorResponse.errors.length > 0) {
				errorMessage = errorResponse.errors[0];
			} else if (errorResponse?.message) {
				errorMessage = errorResponse.message;
			}

			toast.error(errorMessage);
		} finally {
			setState((prev) => ({ ...prev, loading: false }));
		}
	};

	return (
		<div className="md:w-1/2 w-full flex justify-center items-center md:px-8 md:max-h-screen overflow-auto">
			<div className="p-6 md:p-8 flex flex-col justify-center items-center gap-3 md:gap-4 text-black text-center max-w-[480px] w-full bg-white md:bg-transparent rounded-2xl md:rounded-none shadow-lg md:shadow-none">
				{/* Heading */}
				<h1 className="text-xl md:text-4xl font-bold font-sk-modernist">
					Join STYOLA Today!
				</h1>

				{/* Subtitle */}
				<p className="text-sm md:text-base font-sk-modernist text-styola-gray mb-1 md:mb-2 text-center">
					Fill in the details below to create your account and start selling!
				</p>

				<form onSubmit={handleSubmit(onSubmit)} className="w-full">
					<div className="flex flex-col gap-3 md:gap-4 w-full">
						{/* Company Name */}
						<Controller
							name="name"
							control={control}
							defaultValue=""
							rules={{
								required: "Name is required",
							}}
							render={({ field }) => (
								<CustomTextInput
									{...field}
									placeholder="Company Name*"
									className="border border-gray-300 rounded-md font-sk-modernist"
									autoFocus
									error={errors.name?.message}
								/>
							)}
						/>
						{/* Email */}
						<Controller
							name="email"
							control={control}
							defaultValue=""
							rules={{
								required: "Email is required.",
							}}
							render={({ field }) => (
								<CustomTextInput
									{...field}
									type="email"
									placeholder="Email Address*"
									className="border border-gray-300 rounded-md font-sk-modernist"
									error={errors.name?.message}
								/>
							)}
						/>
						{/* Email */}
						<Controller
							name="password"
							control={control}
							defaultValue=""
							rules={{
								required: "Password is required.",
							}}
							render={({ field }) => (
								<CustomTextInput
									{...field}
									type="password"
									placeholder="Password*"
									className="border border-gray-300 rounded-md font-sk-modernist"
									error={errors.password?.message}
								/>
							)}
						/>

						<div className="flex justify-between items-center">
							{/* Terms and Policy */}
							<Controller
								name="termsConditons"
								control={control}
								render={({ field }) => (
									<CustomCheckbox
										{...field}
										checked={field.value}
										onChange={field.onChange}
										labelClassName="!text-sm !font-medium font-sk-modernist"
										label={
											<div className="text-left">
												I agree to the{" "}
												<Link
													to={"/FAQs"}
													className="text-styola-primary font-semibold underline font-sk-modernist"
												>
													Terms & Condition{" "}
												</Link>{" "}
												and{" "}
												<Link
													to={"/policy"}
													className="text-styola-primary font-semibold underline font-sk-modernist"
												>
													Privacy Policy
												</Link>
											</div>
										}
										error={errors.termsConditons?.message}
									/>
								)}
							/>
						</div>

						{/* Bot protection: hidden honeypot + Cloudflare Turnstile */}
						<Honeypot value={company} onChange={setCompany} />
						<Turnstile onToken={setTurnstileToken} />

						{/* Create Account Button */}
						<CustomButton
							variant="secondary"
							title="Create Account"
							type="submit"
							className="w-full font-sk-modernist"
							isLoading={state.loading}
							isDisabled={turnstileConfigured && !turnstileToken}
						/>
					</div>
				</form>

				{/* Login Link */}
				<p className="text-sm mt-2 text-center text-black font-sk-modernist">
					Already have an account?{" "}
					<Link to="/login" className="text-styola-primary font-bold font-sk-modernist">
						Login
					</Link>
				</p>
			</div>
		</div>
	);
};

export default RegisterForm;
